import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from '@itsrever/design-system'
import { useTranslation } from 'react-i18next'
import {
  PbSendTestWebhookRequestWebhookEnum,
  type PbSendTestWebhookResponse
} from '@itsrever/rever-api'
import {
  getWebhookSubscriptions,
  sendTestWebhook,
  upsertWebhookSubscriptions
} from '@/api/settings-api'

export interface WebhookSubscriptions {
  onStartEnabled: boolean
  onCompletedEnabled: boolean
  url: string
}

const WEBHOOK_SUBSCRIPTIONS_KEY = ['webhookSubscriptions']

function subscriptionsKey(ecommerceId?: string) {
  return [...WEBHOOK_SUBSCRIPTIONS_KEY, ecommerceId]
}

export function useWebhookSubscriptions(ecommerceId: string, enabled: boolean) {
  const query = useQuery({
    queryKey: subscriptionsKey(ecommerceId),
    queryFn: () => getWebhookSubscriptions(ecommerceId),
    enabled: enabled && !!ecommerceId
  })

  const subscriptions: WebhookSubscriptions = {
    onStartEnabled: query.data?.on_start_enabled ?? false,
    onCompletedEnabled: query.data?.on_completed_enabled ?? false,
    url: query.data?.url ?? ''
  }

  return {
    subscriptions,
    loadingSubscriptions: query.isLoading,
    subscriptionsError: query.isError,
    reloadSubscriptions: query.refetch
  }
}

export function useSaveWebhookSubscriptions(ecommerceId: string) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: (subscriptions: WebhookSubscriptions) =>
      upsertWebhookSubscriptions(ecommerceId, subscriptions),
    onSuccess: () => {
      toast({ text: t('webhook_subscriptions.saved'), variant: 'success' })
      queryClient.invalidateQueries({ queryKey: subscriptionsKey(ecommerceId) })
    },
    onError: () =>
      toast({ text: t('webhook_subscriptions.save_error'), variant: 'error' })
  })

  return {
    saveSubscriptions: mutation.mutateAsync,
    saving: mutation.isPending
  }
}

export type TestableWebhook = 'onStart' | 'onCompleted'

export interface WebhookTestResult {
  webhook: TestableWebhook
  // Undefined when the request to REVER failed, so nothing is known about the endpoint.
  response?: PbSendTestWebhookResponse
}

const TEST_WEBHOOK_TYPES: Record<
  TestableWebhook,
  PbSendTestWebhookRequestWebhookEnum
> = {
  onStart: PbSendTestWebhookRequestWebhookEnum.OnStart,
  onCompleted: PbSendTestWebhookRequestWebhookEnum.OnCompleted
}

// One request per webhook, so each answer is reported against the event it sampled. Settled
// rather than all: one failed request must not hide a sample the endpoint did receive.
export function useSendTestWebhooks(ecommerceId: string) {
  const mutation = useMutation({
    mutationFn: async ({
      webhooks,
      url
    }: {
      webhooks: TestableWebhook[]
      url: string
    }): Promise<WebhookTestResult[]> => {
      const outcomes = await Promise.allSettled(
        webhooks.map((webhook) =>
          sendTestWebhook(ecommerceId, TEST_WEBHOOK_TYPES[webhook], url)
        )
      )
      return outcomes.map((outcome, i) => ({
        webhook: webhooks[i],
        response: outcome.status === 'fulfilled' ? outcome.value : undefined
      }))
    }
  })

  return {
    sendTests: mutation.mutate,
    testing: mutation.isPending,
    testResults: mutation.data,
    clearTestResults: mutation.reset
  }
}
